import { useState, useEffect } from 'react';
import { useMoanaPaye } from '../../contexts/MoanaPayeContext';
import { Settings, Calendar, Edit2, Check, X } from 'lucide-react';

export default function ParametresPage() {
  const { mpFetch, user } = useMoanaPaye();
  const [tab, setTab] = useState('cps');
  const [cotisations, setCotisations] = useState([]);
  const [tranches, setTranches] = useState([]);
  const [secteurs, setSecteurs] = useState([]);
  const [feries, setFeries] = useState([]);
  const [plafonds, setPlafonds] = useState([]);
  const [editCot, setEditCot] = useState(null);
  const [editMotif, setEditMotif] = useState('');

  useEffect(() => {
    mpFetch('/api/moana-paye/parametres/cotisations-cps').then(setCotisations).catch(console.error);
    mpFetch('/api/moana-paye/parametres/tranches-cst').then(setTranches).catch(console.error);
    mpFetch('/api/moana-paye/parametres/secteurs-cps').then(setSecteurs).catch(console.error);
    mpFetch('/api/moana-paye/parametres/jours-feries/2026').then(setFeries).catch(console.error);
    mpFetch('/api/moana-paye/parametres/plafonds').then(setPlafonds).catch(console.error);
  }, []);

  const isAdmin = user?.profil === 'super_admin' || user?.profil === 'admin';

  const saveEditCot = async () => {
    if (!editMotif.trim()) return alert('Motif obligatoire');
    try {
      await mpFetch(`/api/moana-paye/parametres/cotisations-cps/${editCot.id}`, {
        method: 'PUT',
        body: JSON.stringify({ taux_salarial: editCot.taux_salarial, taux_patronal: editCot.taux_patronal, motif: editMotif })
      });
      mpFetch('/api/moana-paye/parametres/cotisations-cps').then(setCotisations);
      setEditCot(null);
      setEditMotif('');
    } catch (err) { alert(err.message); }
  };

  const tabs = [
    { key: 'cps', label: 'Cotisations CPS' },
    { key: 'cst', label: 'Tranches CST' },
    { key: 'secteurs', label: 'Secteurs CPS' },
    { key: 'plafonds', label: 'Plafonds' },
    { key: 'feries', label: 'Jours fériés PF' },
  ];

  const fmt = (v) => v != null ? Number(v).toLocaleString('fr-FR') : '-';

  return (
    <div data-testid="mp-parametres">
      <h2 className="text-xl font-bold text-white mb-6">Parametres sociaux 2026</h2>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-[#1a2332] rounded-lg p-1 border border-white/10 overflow-x-auto">
        {tabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} className={`px-4 py-2 text-sm rounded-md whitespace-nowrap transition-colors ${tab === t.key ? 'bg-[#2D5A5A] text-white' : 'text-slate-400 hover:text-white'}`} data-testid={`mp-tab-${t.key}`}>{t.label}</button>
        ))}
      </div>

      {/* CPS */}
      {tab === 'cps' && (
        <div className="bg-[#1a2332] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10">
              <th className="text-left px-4 py-3 text-slate-500 text-xs font-medium">Branche</th>
              <th className="text-left px-4 py-3 text-slate-500 text-xs font-medium">Libelle</th>
              <th className="text-right px-4 py-3 text-slate-500 text-xs font-medium">Taux salarial</th>
              <th className="text-right px-4 py-3 text-slate-500 text-xs font-medium">Taux patronal</th>
              <th className="text-right px-4 py-3 text-slate-500 text-xs font-medium">Total</th>
              {isAdmin && <th className="px-4 py-3 w-10"></th>}
            </tr></thead>
            <tbody>
              {cotisations.map(c => (
                <tr key={c.id} className="border-b border-white/5 hover:bg-white/3">
                  <td className="px-4 py-3 text-[#E8A020] font-mono text-xs">{c.branche}</td>
                  <td className="px-4 py-3 text-white">{c.libelle}</td>
                  <td className="px-4 py-3 text-right text-white">{Number(c.taux_salarial).toFixed(2)}%</td>
                  <td className="px-4 py-3 text-right text-white">{Number(c.taux_patronal).toFixed(2)}%</td>
                  <td className="px-4 py-3 text-right text-slate-400">{(Number(c.taux_salarial) + Number(c.taux_patronal)).toFixed(2)}%</td>
                  {isAdmin && <td className="px-4 py-3"><button onClick={() => { setEditCot({ ...c }); setEditMotif(''); }} className="text-slate-600 hover:text-[#E8A020]"><Edit2 className="w-3.5 h-3.5" /></button></td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* CST */}
      {tab === 'cst' && (
        <div className="bg-[#1a2332] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10">
              <th className="text-left px-4 py-3 text-slate-500 text-xs font-medium">Tranche</th>
              <th className="text-right px-4 py-3 text-slate-500 text-xs font-medium">De (F CFP)</th>
              <th className="text-right px-4 py-3 text-slate-500 text-xs font-medium">A (F CFP)</th>
              <th className="text-right px-4 py-3 text-slate-500 text-xs font-medium">Taux</th>
            </tr></thead>
            <tbody>
              {tranches.map(t => (
                <tr key={t.id} className="border-b border-white/5">
                  <td className="px-4 py-3 text-[#E8A020] font-mono text-xs">{t.numero_tranche}</td>
                  <td className="px-4 py-3 text-right text-white">{fmt(t.montant_de)} F</td>
                  <td className="px-4 py-3 text-right text-white">{t.montant_a ? fmt(t.montant_a) + ' F' : 'Sans limite'}</td>
                  <td className="px-4 py-3 text-right text-white font-medium">{Number(t.taux).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 text-xs text-slate-600 border-t border-white/5">
            Seuil non-taxation: revenus &lt; 150 000 F CFP. CST &lt; 750 F: non precomptee.
          </div>
        </div>
      )}

      {/* Secteurs */}
      {tab === 'secteurs' && (
        <div className="bg-[#1a2332] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10">
              <th className="text-left px-4 py-3 text-slate-500 text-xs font-medium">Code</th>
              <th className="text-left px-4 py-3 text-slate-500 text-xs font-medium">Secteur</th>
              <th className="text-right px-4 py-3 text-slate-500 text-xs font-medium">FSR</th>
              <th className="text-right px-4 py-3 text-slate-500 text-xs font-medium">Prest. fam.</th>
              <th className="text-right px-4 py-3 text-slate-500 text-xs font-medium">AVTS</th>
              <th className="text-right px-4 py-3 text-slate-500 text-xs font-medium">AT</th>
            </tr></thead>
            <tbody>
              {secteurs.map(s => (
                <tr key={s.id} className="border-b border-white/5">
                  <td className="px-4 py-3 text-[#E8A020] font-mono text-xs">{s.code_secteur}</td>
                  <td className="px-4 py-3 text-white">{s.libelle_secteur}</td>
                  <td className="px-4 py-3 text-right text-white">{Number(s.taux_fsr).toFixed(2)}%</td>
                  <td className="px-4 py-3 text-right text-white">{Number(s.taux_prest_fam).toFixed(2)}%</td>
                  <td className="px-4 py-3 text-right text-white">{Number(s.taux_avts).toFixed(2)}%</td>
                  <td className="px-4 py-3 text-right text-white">{Number(s.taux_at).toFixed(2)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Plafonds */}
      {tab === 'plafonds' && (
        <div className="bg-[#1a2332] border border-white/10 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-white/10">
              <th className="text-left px-4 py-3 text-slate-500 text-xs font-medium">Code</th>
              <th className="text-left px-4 py-3 text-slate-500 text-xs font-medium">Libelle</th>
              <th className="text-right px-4 py-3 text-slate-500 text-xs font-medium">Montant (F CFP)</th>
            </tr></thead>
            <tbody>
              {plafonds.map((p, i) => (
                <tr key={i} className="border-b border-white/5">
                  <td className="px-4 py-3 text-[#E8A020] font-mono text-xs">{p.code}</td>
                  <td className="px-4 py-3 text-white">{p.libelle}</td>
                  <td className="px-4 py-3 text-right text-white font-medium">{fmt(p.valeur_num)} F</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Jours feries */}
      {tab === 'feries' && (
        <div className="bg-[#1a2332] border border-white/10 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 text-white text-sm font-medium flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#E8A020]" /> 13 jours fériés PF — 2026
          </div>
          <div className="divide-y divide-white/5">
            {feries.map(f => (
              <div key={f.id} className="px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-white font-mono text-sm w-24">{f.date_ferie}</span>
                  <span className="text-white text-sm">{f.libelle}</span>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${f.type === 'fixe' ? 'bg-[#2D5A5A]/20 text-[#2D5A5A]' : 'bg-[#E8A020]/15 text-[#E8A020]'}`}>{f.type}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Modal edit cotisation */}
      {editCot && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4" onClick={() => setEditCot(null)}>
          <div className="bg-[#1a2332] border border-white/10 rounded-xl p-5 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-semibold mb-1">Modifier : {editCot.libelle}</h3>
            <p className="text-slate-500 text-xs mb-4">Toute modification est tracee (audit trail)</p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="text-slate-400 text-xs mb-1 block">Taux salarial (%)</label>
                <input type="number" step="0.01" value={editCot.taux_salarial} onChange={e => setEditCot(c => ({ ...c, taux_salarial: parseFloat(e.target.value) }))} className="w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
              </div>
              <div>
                <label className="text-slate-400 text-xs mb-1 block">Taux patronal (%)</label>
                <input type="number" step="0.01" value={editCot.taux_patronal} onChange={e => setEditCot(c => ({ ...c, taux_patronal: parseFloat(e.target.value) }))} className="w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
              </div>
            </div>
            <div className="mb-4">
              <label className="text-slate-400 text-xs mb-1 block">Motif de modification <span className="text-[#E8A020]">*</span></label>
              <input value={editMotif} onChange={e => setEditMotif(e.target.value)} placeholder="Ex: Arrete n 2554 CM du 19/12/2025" className="w-full bg-[#0f1520] border border-white/10 rounded-lg px-3 py-2 text-white text-sm" />
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={() => setEditCot(null)} className="px-4 py-2 text-sm text-slate-400">Annuler</button>
              <button onClick={saveEditCot} className="px-4 py-2 bg-[#E8A020] text-[#1a2332] text-sm font-medium rounded-lg flex items-center gap-1"><Check className="w-3.5 h-3.5" />Valider</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
